import { useCallback, useEffect, useState } from "react";
import { generatePath } from "react-router-dom";
import { useHistory, useLocation } from "react-router-dom";
import { type RouteParams } from "shared/api/types/society/[societyId]/app-route-config";
import {
  CustomRouterContext,
  type CustomRouterType,
} from "shared/context/CustomRouterContext";
import { useIFrameRouterRedirect } from "hub/src/hooks/iframe/router/useIFrameRouterRedirect";

export const determinParamType = (value: string) => {
  if (!isNaN(Number(value))) {
    return Number(value);
  } else if (value === "true" || value === "false") {
    return value === "true";
  }
  if (value === "null") {
    return null;
  } else {
    return value;
  }
};

const getCustomSearch = (search: string) => {
  return Array.from(new URLSearchParams(search).entries()).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: determinParamType(value),
    }),
    {},
  );
};

export const UserHubCustomRouterProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const location = useLocation();
  const history = useHistory();
  const [customState, setCustomState] = useState<RouteParams>({});
  const [customSearch, setCustomSearch] = useState<RouteParams>(() => {
    if (location.search) {
      const parsed = getCustomSearch(location.search);
      console.log("[Pillar Router] initial customSearch from URL search:", {
        search: location.search,
        parsed,
      });
      return parsed;
    }
    console.log("[Pillar Router] initial customSearch: empty (no URL search)");
    return {};
  });
  useIFrameRouterRedirect();

  useEffect(() => {
    if (location.search) {
      const parsed = getCustomSearch(location.search);
      console.log("[Pillar Router] customSearch updated from URL search:", {
        search: location.search,
        parsed,
      });
      setCustomSearch(parsed);
    } else {
      console.log(
        "[Pillar Router] customSearch NOT cleared - location.search is empty, retaining:",
        customSearch,
      );
    }
  }, [location.search]);
  useEffect(() => {
    if (location.state) {
      const newCustomState = Object.entries(location.state).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: determinParamType(value),
        }),
        {},
      );
      setCustomState(newCustomState);
    }
  }, [location.state]);
  const combinedState: RouteParams = {
    ...customState,
    ...customSearch,
  };
  const router: CustomRouterType = {
    asPath: `${location.pathname}${location.search}`,
    pathname: location.pathname,
    state: combinedState,
    isReady: true,
    replace: (path: string, params?: RouteParams) => {
      setCustomState((prev) => {
        return { ...prev, ...params };
      });
      history.replace(generatePath(path, params as any));
    },
    push: useCallback(
      (path: string, params?: RouteParams) => {
        {
          console.log("[Pillar Router] push:", {
            path,
            params,
            currentCombinedState: combinedState,
          });
          setCustomState((prev) => {
            return { ...prev, ...params };
          });
          history.push(generatePath(path, params as any));
        }
      },
      [history, setCustomState, customState],
    ),
    setInState: (key: string, value: string) => {
      setCustomState((prev) => ({ ...prev, [key]: value }));
    },
    prefetch: (url: string) => {
      // This is a placeholder for prefetching logic.
      // throw new Error("Prefetch not implemented on userhub");
    },
    removeFromState: useCallback(
      (key: string) => {
        const { [key]: thingToBeRemoved, ...restOfState } = customState;
        setCustomState(restOfState);
        const { [key]: otherThingToBeRemoved, ...restOfsearch } = customSearch;
        setCustomSearch(restOfsearch);

        // Create a new state object without the property to remove.
        const newLocationState = location.state ? { ...location.state } : {};
        delete (newLocationState as any)[key];

        // Use history.replace to update the current route with the new state.
        // This will cause location.state to update in the next render.
        history.replace(location.pathname, newLocationState);
      },
      [
        setCustomSearch,
        setCustomState,
        customSearch,
        customState,
        combinedState,
      ],
    ),
  };
  return (
    <CustomRouterContext.Provider value={router}>
      {children}
    </CustomRouterContext.Provider>
  );
};
